/**
 * UserReportPrint - Full printable view of a User Report
 *
 * Uses paper-css for A4 page layout. Renders all report data
 * across multiple pages with proper page breaks.
 *
 * Usage:
 *   <UserReportPrint
 *     userId="..."
 *     organisationId="..."
 *     reportSlug="..."
 *     onClose={() => {}}
 *   />
 */
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { getAnswerColor } from "@/components/QuestionnaireReport";
import SnpImpactSymbol from "@/components/diagrams/shared/SnpImpactSymbol";
import { formatFunctionalMarkerSubtitle } from "@/components/reports/InterpretationSharedRows";
import { toTitleCase } from "@/core/utils";
import getSnpColor, {
  buildDiagramGeneColors,
  getSnpImpactSymbol,
  SNP_UNTESTED_COLOR
} from "@/utils/getSnpColor";
import { getInterpretationColor } from "@/utils/shared";
import { useQuery } from "@apollo/client";

import Cover from "./Cover";
import PaginatedContent from "./Paginator";
import "./paper.css";

// Lazy-import diagram SVG components (the bare SVGs inside each subdirectory,
// not the WithToggleKey wrappers at the top level).
const DIAGRAM_COMPONENTS = {};
const diagramModules = import.meta.glob("@/components/diagrams/*DiagramKey/index.jsx", {
  eager: true
});
Object.entries(diagramModules).forEach(([path, mod]) => {
  // Extract directory name e.g. "CardiometabolicAppetiteControlDiagramKey"
  const parts = path.split("/");
  const name = parts[parts.length - 2];
  DIAGRAM_COMPONENTS[name] = mod.default;
});

function resultHasDiagram(result) {
  return Boolean(result?.diagram && DIAGRAM_COMPONENTS[result.diagram]);
}

function resultPrimaryAnchor(resultId, hasDiagram) {
  return hasDiagram ? `#result-${resultId}-intro` : `#result-${resultId}-markers`;
}

function userResultHasMarkerContent(userResult) {
  const countGroups = [
    userResult.dnaDatapointCounts,
    userResult.functionalDatapointCounts,
    userResult.symptomCounts
  ];
  return countGroups.some(
    c => (c?.inRangeCount || 0) + (c?.outOfRangeCount || 0) + (c?.untestedCount || 0) > 0
  );
}

function panelHasDiagram(panel) {
  return Boolean(panel?.diagram && DIAGRAM_COMPONENTS[panel.diagram]);
}

function getPrintPanels(report) {
  return report?.reportInformationPanels || [];
}

function partitionPrintPanels(panels) {
  const sorted = [...panels].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const atStart = [];
  const afterResult = [];

  for (const panel of sorted) {
    const afterResultKey = panel.pdf?.after_result;
    if (afterResultKey != null && afterResultKey !== "") {
      afterResult.push({ panel, afterResultKey: String(afterResultKey) });
    } else {
      atStart.push(panel);
    }
  }

  return { atStart, afterResult };
}

function resultMatchesAfterResultKey(result, afterResultKey) {
  return (
    String(result.id) === afterResultKey ||
    result.slug === afterResultKey ||
    result.name === afterResultKey
  );
}

function getPanelsAfterResult(afterResultEntries, result) {
  return afterResultEntries
    .filter(entry => resultMatchesAfterResultKey(result, entry.afterResultKey))
    .map(entry => entry.panel);
}

function getReportPrintVariables({ reportSlug, userId, organisationId }) {
  return {
    subsectorSlug: reportSlug,
    ...(userId != null && { userId }),
    ...(organisationId != null && { organisationId })
  };
}

function getUserResultPrintVariables(userResultId, { userId, organisationId }) {
  if (organisationId != null) {
    return { userResultId, userId, organisationId };
  }
  if (userId != null) {
    return { userResultId, userId };
  }
  return { userResultId };
}

/**
 * Hide the app chrome (header, nav) when the print view is mounted.
 * Adds a class to <body> that CSS uses to hide everything else.
 */
function useFullscreenPrint() {
  useEffect(() => {
    document.body.classList.add("print-view-active");
    return () => document.body.classList.remove("print-view-active");
  }, []);
}

function sanitizeForFilename(value) {
  return value
    .trim()
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, " ");
}

function buildPrintDocumentTitle({ reportName, patientName, reportDate }) {
  const date = (reportDate || new Date().toLocaleDateString("en-GB")).replace(/\//g, "-");
  return [
    sanitizeForFilename(reportName || "Report"),
    sanitizeForFilename(patientName || "Patient"),
    date
  ].join(" - ");
}

function usePrintDocumentTitle(title) {
  useEffect(() => {
    if (!title) return undefined;
    const previousTitle = document.title;
    document.title = title;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);
}

const PrintReadinessContext = createContext(null);

function PrintReadinessProvider({ sectionIds, children }) {
  const readyRef = useRef(new Set());
  const [isReportReady, setIsReportReady] = useState(false);
  const sectionIdsKey = sectionIds.join("|");

  const checkReady = useCallback(() => {
    const ready = sectionIds.length > 0 && sectionIds.every(id => readyRef.current.has(id));
    setIsReportReady(ready);
  }, [sectionIds]);

  const markReady = useCallback(
    id => {
      readyRef.current.add(id);
      checkReady();
    },
    [checkReady]
  );

  const markNotReady = useCallback(id => {
    readyRef.current.delete(id);
    setIsReportReady(false);
  }, []);

  useEffect(() => {
    readyRef.current = new Set();
    setIsReportReady(false);
  }, [sectionIdsKey]);

  return (
    <PrintReadinessContext.Provider value={{ isReportReady, markReady, markNotReady }}>
      {children}
    </PrintReadinessContext.Provider>
  );
}

function usePrintSectionReady(sectionId, isReady) {
  const context = useContext(PrintReadinessContext);

  useEffect(() => {
    if (!context) return undefined;
    if (isReady) context.markReady(sectionId);
    else context.markNotReady(sectionId);
  }, [context, sectionId, isReady]);
}

function usePrintReadiness() {
  return useContext(PrintReadinessContext)?.isReportReady ?? false;
}

const REGENERUS_LOGO_SRC = "/email-images/regenerus-logo-for-email-template.svg";

function tabLabel(name) {
  const stripped = name.replace(/\s*\(.*?\)/g, "").trim();
  return stripped.length > 30 ? stripped.slice(0, 30) + "…" : stripped;
}

function ResultTabs({ allResults, currentResultId }) {
  return (
    <div className="result-tabs">
      {allResults.map(r => (
        <a
          key={r.id}
          href={resultPrimaryAnchor(r.id, r.hasDiagram)}
          className={`result-tab${r.id === currentResultId ? " result-tab-active" : ""}`}
        >
          <span>{tabLabel(r.name)}</span>
        </a>
      ))}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function FooterField({ label, value }) {
  return (
    <span className="footer-field">
      {label} <span className="footer-separator">|</span> {value || "—"}
    </span>
  );
}

function PageFooter({ patientName, dateOfBirth, sex, reportDate }) {
  return (
    <div className="page-footer">
      <div className="footer-logo">
        <img src={REGENERUS_LOGO_SRC} alt="Regenerus Labs" />
      </div>
      <FooterField label="User" value={patientName} />
      <FooterField label="DOB" value={dateOfBirth} />
      <FooterField label="Sex" value={sex} />
      <FooterField label="Report Date" value={reportDate} />
      <span className="page-number" aria-hidden="true" />
    </div>
  );
}

function getDiagramViewBoxSize(svg) {
  const viewBox = svg.viewBox.baseVal;
  if (viewBox.width > 0 && viewBox.height > 0) {
    return { width: viewBox.width, height: viewBox.height };
  }

  try {
    const bbox = svg.getBBox();
    if (bbox.width > 0 && bbox.height > 0) {
      return { width: bbox.width, height: bbox.height };
    }
  } catch {
    // getBBox can fail before the SVG is painted
  }

  return null;
}

function resetDiagramFitStyles(svg) {
  svg.style.width = "";
  svg.style.height = "";
  svg.style.maxWidth = "";
  svg.style.maxHeight = "";
}

function getIntroDiagramSvg(rootEl) {
  return rootEl.querySelector(".result-diagram > svg");
}

function findLandscapeWrap(svg) {
  return Array.from(svg.children).find(
    el => el.getAttribute?.("data-print-landscape-wrap") === "true"
  );
}

/**
 * Rotate landscape diagram content inside the same SVG.
 * Firefox print preview ignores CSS transforms and also ignores a
 * transform on a <g> that wraps a nested <svg>, so we must not nest.
 */
function applyLandscapeSvgRotation(svg, box) {
  const { x, y, width: w, height: h } = box;
  if (!(w > h)) return false;

  const cx = x + w / 2;
  const cy = y + h / 2;
  let wrap = findLandscapeWrap(svg);

  if (!wrap) {
    wrap = document.createElementNS("http://www.w3.org/2000/svg", "g");
    wrap.setAttribute("data-print-landscape-wrap", "true");
    while (svg.firstChild) {
      wrap.appendChild(svg.firstChild);
    }
    svg.appendChild(wrap);
  }

  wrap.setAttribute("transform", `rotate(90 ${cx} ${cy})`);
  svg.setAttribute("viewBox", `${cx - h / 2} ${cy - w / 2} ${h} ${w}`);
  svg.removeAttribute("width");
  svg.removeAttribute("height");
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  return true;
}

function fitIntroDiagram(rootEl, svg, landscapeBox) {
  const diagramEl = rootEl.querySelector(".result-diagram");
  if (!diagramEl || !svg) return;

  const availableWidth = diagramEl.clientWidth;
  let availableHeight = diagramEl.clientHeight;

  const sheetEl = rootEl.closest(".sheet");
  const footerEl = sheetEl?.querySelector(".page-footer");
  if (footerEl) {
    const footerTop = footerEl.getBoundingClientRect().top;
    const diagramTop = diagramEl.getBoundingClientRect().top;
    const gapPx = 8;
    const heightToFooter = footerTop - diagramTop - gapPx;
    if (heightToFooter > 0) {
      availableHeight = Math.min(availableHeight, heightToFooter);
    }
  }

  if (availableWidth <= 0 || availableHeight <= 0) return;

  if (landscapeBox && landscapeBox.width > landscapeBox.height) {
    applyLandscapeSvgRotation(svg, landscapeBox);
  }

  const viewBox = getDiagramViewBoxSize(svg);
  if (!viewBox) return;

  const { width: vbWidth, height: vbHeight } = viewBox;

  resetDiagramFitStyles(svg);

  const scale = Math.min(availableWidth / vbWidth, availableHeight / vbHeight, 1);
  svg.style.width = `${vbWidth * scale}px`;
  svg.style.height = `${vbHeight * scale}px`;
}

function ResultIntroDiagram({
  title,
  body,
  DiagramComponent,
  geneColors,
  expectedSnps,
  onReady,
  showTitle = true,
  showBody = true,
  showDiagram = true
}) {
  const ref = useRef(null);
  const diagramBoxRef = useRef(null);
  const [isLandscape, setIsLandscape] = useState(false);
  const [diagramBox, setDiagramBox] = useState(null);
  const [diagramReady, setDiagramReady] = useState(false);
  const shouldRenderDiagram = showDiagram && DiagramComponent;
  diagramBoxRef.current = diagramBox;

  useEffect(() => {
    if (!ref.current) {
      return undefined;
    }

    if (!shouldRenderDiagram) {
      setDiagramReady(false);
      setIsLandscape(false);
      setDiagramBox(null);

      let cancelled = false;
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) onReady?.();
        });
      });

      return () => {
        cancelled = true;
        cancelAnimationFrame(raf);
      };
    }

    let cancelled = false;

    const prepareDiagram = () => {
      if (cancelled || !ref.current) return;

      const svg = getIntroDiagramSvg(ref.current);
      if (!svg) {
        onReady?.();
        return;
      }

      try {
        const wrap = findLandscapeWrap(svg);
        const bbox = (wrap || svg).getBBox();
        if (bbox.width <= 0 || bbox.height <= 0) {
          onReady?.();
          return;
        }

        const landscape = bbox.width > bbox.height;
        if (!landscape) {
          svg.setAttribute("viewBox", `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
          svg.removeAttribute("height");
          svg.removeAttribute("width");
        }

        setDiagramBox({ x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height });
        setIsLandscape(landscape);
        setDiagramReady(true);
      } catch {
        setDiagramReady(false);
        onReady?.();
      }
    };

    setDiagramReady(false);
    setIsLandscape(false);
    setDiagramBox(null);

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(prepareDiagram);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [shouldRenderDiagram, DiagramComponent, geneColors, expectedSnps, onReady, body]);

  useLayoutEffect(() => {
    if (!shouldRenderDiagram || !diagramReady || !ref.current) return undefined;

    const layoutDiagram = () => {
      if (!ref.current) return;
      const svg = getIntroDiagramSvg(ref.current);
      if (svg) fitIntroDiagram(ref.current, svg, diagramBoxRef.current);
    };

    layoutDiagram();
    onReady?.();

    window.addEventListener("beforeprint", layoutDiagram);
    return () => window.removeEventListener("beforeprint", layoutDiagram);
  }, [
    shouldRenderDiagram,
    diagramReady,
    isLandscape,
    diagramBox,
    DiagramComponent,
    geneColors,
    expectedSnps,
    onReady
  ]);

  return (
    <div
      className={`result-intro-diagram${
        shouldRenderDiagram && !showTitle && !showBody ? " result-intro-diagram-only" : ""
      }`}
      ref={ref}
    >
      {showTitle && title ? (
        <div className="result-header">
          <h2>{title}</h2>
        </div>
      ) : null}
      {showBody && body ? (
        <div
          className="result-description information-panel-body"
          dangerouslySetInnerHTML={{ __html: body }}
        />
      ) : null}
      {shouldRenderDiagram ? (
        <div className={`result-diagram${isLandscape ? " result-diagram-landscape" : ""}`}>
          <DiagramComponent geneColors={geneColors} expectedSnps={expectedSnps} />
        </div>
      ) : null}
    </div>
  );
}

function PanelPrintSheetPage({
  panelId,
  sectionType,
  elementId,
  sheetClassName,
  tabs,
  footerEl,
  children
}) {
  return (
    <div
      className={`sheet padding-15mm ${sheetClassName}`}
      data-section={`panel-${panelId}`}
      data-section-type={sectionType}
      id={elementId}
    >
      {tabs}
      <div className="result-intro-body">
        <div className="result-section">{children}</div>
      </div>
      {footerEl}
    </div>
  );
}

function InformationPanelPrintSheet({
  panel,
  snpsForDiagram,
  expectedSnps,
  allResults,
  currentResultId,
  patientName,
  dateOfBirth,
  sex,
  reportDate
}) {
  const DiagramComponent = panelHasDiagram(panel) ? DIAGRAM_COMPONENTS[panel.diagram] : null;
  const hasDiagram = Boolean(DiagramComponent);
  const geneColors = useMemo(
    () => buildDiagramGeneColors(snpsForDiagram, expectedSnps),
    [snpsForDiagram, expectedSnps]
  );
  const [textReady, setTextReady] = useState(false);
  const [diagramReady, setDiagramReady] = useState(!hasDiagram);
  const handleTextReady = useCallback(() => setTextReady(true), []);
  const handleDiagramReady = useCallback(() => setDiagramReady(true), []);

  useEffect(() => {
    setTextReady(false);
    setDiagramReady(!hasDiagram);
  }, [panel.id, hasDiagram, snpsForDiagram, expectedSnps]);

  const ready = textReady && diagramReady;
  usePrintSectionReady(`panel-${panel.id}`, ready);

  const footerEl = (
    <PageFooter
      patientName={patientName}
      dateOfBirth={dateOfBirth}
      sex={sex}
      reportDate={reportDate}
    />
  );
  const tabs = <ResultTabs allResults={allResults} currentResultId={currentResultId} />;
  const textContent = (
    <ResultIntroDiagram
      title={panel.title}
      body={panel.body}
      showDiagram={false}
      onReady={handleTextReady}
    />
  );

  if (!hasDiagram) {
    return (
      <PanelPrintSheetPage
        panelId={panel.id}
        sectionType="information-panel"
        elementId={`panel-${panel.id}`}
        sheetClassName="information-panel-sheet"
        tabs={tabs}
        footerEl={footerEl}
      >
        {textContent}
      </PanelPrintSheetPage>
    );
  }

  return (
    <>
      <PanelPrintSheetPage
        panelId={panel.id}
        sectionType="information-panel"
        elementId={`panel-${panel.id}`}
        sheetClassName="information-panel-sheet"
        tabs={tabs}
        footerEl={footerEl}
      >
        {textContent}
      </PanelPrintSheetPage>
      <PanelPrintSheetPage
        panelId={panel.id}
        sectionType="information-panel-diagram"
        elementId={`panel-${panel.id}-diagram`}
        sheetClassName="result-intro-sheet information-panel-diagram-sheet"
        tabs={tabs}
        footerEl={footerEl}
      >
        <ResultIntroDiagram
          DiagramComponent={DiagramComponent}
          geneColors={geneColors}
          expectedSnps={expectedSnps}
          showTitle={false}
          showBody={false}
          onReady={handleDiagramReady}
        />
      </PanelPrintSheetPage>
    </>
  );
}

function getSnpAccentColor(datapoint) {
  const value = datapoint?.riskAllele?.value ?? datapoint?.value;
  const color = getSnpColor(value);
  return color === "dark" ? SNP_UNTESTED_COLOR : color;
}

function MarkerSectionBadge({ children }) {
  return <div className="marker-section-badge">{children}</div>;
}

function ResultMarkersHeader({ resultName, description }) {
  return (
    <div className="result-markers-header">
      <h2>{resultName}</h2>
      {description && (
        <div className="result-description" dangerouslySetInnerHTML={{ __html: description }} />
      )}
    </div>
  );
}

function MarkerResultCell({ label, accentColor, variant = "filled", symbol, suffix }) {
  const style = variant === "filled" && accentColor ? { backgroundColor: accentColor } : undefined;

  return (
    <td className="marker-value">
      <div className="marker-result-inner">
        <div className="marker-result-badge-row">
          <span className={`marker-result-badge marker-result-badge--${variant}`} style={style}>
            {label}
          </span>
          {symbol ? <SnpImpactSymbol symbol={symbol} fill="#1a1a1a" size={14} /> : null}
        </div>
        {suffix ? <span className="marker-result-suffix">{suffix}</span> : null}
      </div>
    </td>
  );
}

function getSnpResultLabel(datapoint) {
  return datapoint?.riskAllele?.alleles?.join("") ?? datapoint?.value ?? "-";
}

function getSnpResultSymbol(datapoint) {
  const value = datapoint?.riskAllele?.value ?? datapoint?.value;
  return getSnpImpactSymbol(value, datapoint?.impactActivity);
}

function parseSnpName(name) {
  const parts = name.split("-").map(part => part.trim());
  if (parts.length < 2 || !parts[1]) {
    return { primary: name, rsId: null };
  }

  const rsPart = parts[1].replace(/^rs/i, "");
  return {
    primary: parts[0],
    rsId: `rs${rsPart}`
  };
}

function SnpMarkerNameCell({ name, accentColor }) {
  const { primary, rsId } = parseSnpName(name);

  return (
    <td className="marker-name">
      <div className="marker-name-inner">
        <span className="marker-accent-stripe" style={{ backgroundColor: accentColor }} />
        <div className="marker-name-text">
          <span>{primary}</span>
          {rsId ? <span className="marker-name-rs">{rsId}</span> : null}
        </div>
      </div>
    </td>
  );
}

function MarkerNameCell({ name, sampleType, dateExtracted, accentColor }) {
  const subtitle = formatFunctionalMarkerSubtitle(sampleType, dateExtracted);

  return (
    <td className="marker-name">
      <div className="marker-name-inner">
        <span className="marker-accent-stripe" style={{ backgroundColor: accentColor }} />
        <div className="marker-name-text">
          <span>{name}</span>
          {subtitle ? <span className="marker-name-sample-type">{subtitle}</span> : null}
        </div>
      </div>
    </td>
  );
}

function MarkersTableHead({ showSuggestions = true, thirdColumnLabel = "Interpretation" }) {
  return (
    <thead>
      <tr>
        <th>Name</th>
        <th>Result</th>
        <th>{thirdColumnLabel}</th>
        {showSuggestions ? <th>Suggestions</th> : null}
      </tr>
    </thead>
  );
}

function joinDatapointDescriptionHtml({ description, function: fn, reason }) {
  const parts = [description, fn, reason].filter(Boolean);
  return parts.length > 0 ? parts.join("<br/><br/>") : null;
}

function MarkerHtmlCell({ html, className = "marker-interpretation" }) {
  if (!html) {
    return <td className={className}>-</td>;
  }
  return <td className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

const SYMPTOM_SCORE_COLORS = {
  green: "#00bf86",
  orange: "#fca557",
  red: "#e34b4b",
  midGrey: "#818181"
};

function getSymptomAnswerColor(score) {
  if (score == null) return SYMPTOM_SCORE_COLORS.midGrey;
  return SYMPTOM_SCORE_COLORS[getAnswerColor(score)] || SYMPTOM_SCORE_COLORS.midGrey;
}

function PotentialSymptomsList({ questionsAndAnswers }) {
  const sorted = [...questionsAndAnswers].sort((a, b) => b.answerScore - a.answerScore);

  return (
    <div className="symptoms-list">
      {sorted.map(qa => (
        <div key={qa.id} className="symptom-row">
          <span className="symptom-title">{toTitleCase(qa.question.symptomGroup)}</span>
          <span className="symptom-answer" style={{ color: getSymptomAnswerColor(qa.answerScore) }}>
            {qa.answerText || "No Answer"}
          </span>
        </div>
      ))}
    </div>
  );
}

function ResultMarkersContent({
  resultName,
  description,
  dnaDatapoints,
  untestedDnaDatapoints,
  functionalDatapoints,
  untestedDatapoints,
  questionsAndAnswers,
  hasDna,
  hasFunctional,
  hasSymptoms
}) {
  return (
    <>
      <ResultMarkersHeader resultName={resultName} description={description} />

      {hasDna && (
        <>
          <MarkerSectionBadge>SNPS</MarkerSectionBadge>
          <table className="markers-table">
            <MarkersTableHead />
            <tbody>
              {dnaDatapoints?.map(dp => (
                <tr key={dp.id}>
                  <SnpMarkerNameCell name={dp.datapoint.name} accentColor={getSnpAccentColor(dp)} />
                  <MarkerResultCell
                    label={getSnpResultLabel(dp)}
                    accentColor={getSnpAccentColor(dp)}
                    symbol={getSnpResultSymbol(dp)}
                  />
                  <MarkerHtmlCell html={dp.snpInterpretation || dp.interpretation} />
                  <MarkerHtmlCell html={dp.snpSuggestion} className="marker-suggestion" />
                </tr>
              ))}
              {untestedDnaDatapoints?.map(dp => (
                <tr key={dp.id}>
                  <SnpMarkerNameCell name={dp.name} accentColor={SNP_UNTESTED_COLOR} />
                  <MarkerResultCell label="no read" variant="dark" />
                  <td className="marker-interpretation">Untested</td>
                  <td className="marker-suggestion">-</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {hasFunctional && (
        <>
          <MarkerSectionBadge>Functional markers</MarkerSectionBadge>
          <table className="markers-table markers-table--three-col">
            <MarkersTableHead showSuggestions={false} thirdColumnLabel="Description" />
            <tbody>
              {functionalDatapoints?.map(dp => (
                <tr key={dp.id}>
                  <MarkerNameCell
                    name={dp.datapoint.name}
                    sampleType={dp.sampleType}
                    dateExtracted={dp.dateExtracted}
                    accentColor={getInterpretationColor(dp.chartConfiguration?.interpretation)}
                  />
                  <MarkerResultCell
                    label={dp.value}
                    accentColor={getInterpretationColor(dp.chartConfiguration?.interpretation)}
                    suffix={dp.datapoint.units || null}
                  />
                  <MarkerHtmlCell html={joinDatapointDescriptionHtml(dp.datapoint)} />
                </tr>
              ))}
              {untestedDatapoints?.map(dp => (
                <tr key={dp.id}>
                  <MarkerNameCell name={dp.name} accentColor="#818181" />
                  <MarkerResultCell label="NOT TESTED" variant="outlined" />
                  <MarkerHtmlCell html={joinDatapointDescriptionHtml(dp)} />
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {hasSymptoms && (
        <>
          <MarkerSectionBadge>Potential symptoms</MarkerSectionBadge>
          <PotentialSymptomsList questionsAndAnswers={questionsAndAnswers} />
        </>
      )}
    </>
  );
}

function ResultSection({
  result,
  dnaDatapoints,
  functionalDatapoints,
  untestedDnaDatapoints,
  untestedDatapoints,
  questionsAndAnswers,
  snpsForDiagram,
  expectedSnps,
  patientName,
  dateOfBirth,
  sex,
  reportDate,
  allResults,
  onLayoutReady
}) {
  const hasDiagram = resultHasDiagram(result);
  const DiagramComponent = hasDiagram ? DIAGRAM_COMPONENTS[result.diagram] : null;
  const geneColors = useMemo(
    () => buildDiagramGeneColors(snpsForDiagram, expectedSnps),
    [snpsForDiagram, expectedSnps]
  );
  const hasDna = dnaDatapoints?.length > 0 || untestedDnaDatapoints?.length > 0;
  const hasFunctional = functionalDatapoints?.length > 0 || untestedDatapoints?.length > 0;
  const hasSymptoms = questionsAndAnswers?.length > 0;
  const hasMarkers = hasDna || hasFunctional || hasSymptoms;
  const [introReady, setIntroReady] = useState(false);
  const [paginatorReady, setPaginatorReady] = useState(!hasMarkers);

  useEffect(() => {
    setIntroReady(false);
    setPaginatorReady(!hasMarkers);
  }, [
    result.id,
    hasMarkers,
    dnaDatapoints,
    functionalDatapoints,
    untestedDnaDatapoints,
    untestedDatapoints,
    questionsAndAnswers
  ]);

  const handleIntroDiagramReady = useCallback(() => {
    setIntroReady(true);
  }, []);

  useEffect(() => {
    if (hasDiagram) return undefined;

    let cancelled = false;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setIntroReady(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [
    hasDiagram,
    result.id,
    hasMarkers,
    dnaDatapoints,
    functionalDatapoints,
    untestedDnaDatapoints,
    untestedDatapoints,
    questionsAndAnswers
  ]);

  useEffect(() => {
    if (introReady && paginatorReady) {
      onLayoutReady?.();
    }
  }, [introReady, paginatorReady, onLayoutReady]);

  const handlePaginatorReady = useCallback(() => {
    setPaginatorReady(true);
  }, []);

  const footerEl = (
    <PageFooter
      patientName={patientName}
      dateOfBirth={dateOfBirth}
      sex={sex}
      reportDate={reportDate}
    />
  );

  const resultId = result.id;

  return (
    <>
      {hasDiagram && (
        <div
          className="sheet padding-15mm result-intro-sheet"
          data-section={`result-${resultId}`}
          data-section-type="intro"
          id={`result-${resultId}-intro`}
        >
          <ResultTabs allResults={allResults} currentResultId={resultId} />
          <div className="result-intro-body">
            <div className="result-section">
              <ResultIntroDiagram
                title={result.name}
                DiagramComponent={DiagramComponent}
                geneColors={geneColors}
                expectedSnps={expectedSnps}
                onReady={handleIntroDiagramReady}
              />
            </div>
          </div>
          {footerEl}
        </div>
      )}

      {/* SNPs, functional markers, and symptoms — paginated together */}
      {hasMarkers && (
        <PaginatedContent
          footer={footerEl}
          padding="15mm"
          sectionAttr={`result-${resultId}`}
          sectionType="markers"
          sectionId={`result-${resultId}-markers`}
          tabs={<ResultTabs allResults={allResults} currentResultId={resultId} />}
          onReady={handlePaginatorReady}
        >
          <ResultMarkersContent
            resultName={result.name}
            description={result.description}
            dnaDatapoints={dnaDatapoints}
            untestedDnaDatapoints={untestedDnaDatapoints}
            functionalDatapoints={functionalDatapoints}
            untestedDatapoints={untestedDatapoints}
            questionsAndAnswers={questionsAndAnswers}
            hasDna={hasDna}
            hasFunctional={hasFunctional}
            hasSymptoms={hasSymptoms}
          />
        </PaginatedContent>
      )}
    </>
  );
}

/**
 * Table of Contents — reads data-section attributes from the DOM after render
 * to determine page numbers. Re-renders once numbers are known.
 */
function TableOfContents({ patientName, dateOfBirth, sex, reportDate, userResults, printPanels }) {
  const [tocItems, setTocItems] = useState(null);
  const containerRef = useRef(null);

  const panelBySectionKey = useMemo(() => {
    const map = new Map();
    for (const panel of printPanels) {
      map.set(`panel-${panel.id}`, panel);
    }
    return map;
  }, [printPanels]);

  usePrintSectionReady("toc", tocItems !== null);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 40;

    const poll = () => {
      attempts++;
      const container = containerRef.current?.closest(".print-container");
      if (!container) {
        if (attempts < maxAttempts) setTimeout(poll, 250);
        return;
      }

      const allPanelsPresent = printPanels.every(panel => {
        const textSheet = container.querySelector(
          `[data-section="panel-${panel.id}"][data-section-type="information-panel"]`
        );
        if (!textSheet) return false;
        if (!panelHasDiagram(panel)) return true;
        return Boolean(
          container.querySelector(
            `[data-section="panel-${panel.id}"][data-section-type="information-panel-diagram"]`
          )
        );
      });

      const allResultsPresent = userResults.every(ur => {
        const sectionKey = `result-${ur.result.id}`;
        if (resultHasDiagram(ur.result)) {
          return container.querySelector(
            `[data-section="${sectionKey}"][data-section-type="intro"]`
          );
        }
        if (!userResultHasMarkerContent(ur)) {
          return true;
        }
        return container.querySelector(
          `[data-section="${sectionKey}"][data-section-type="markers"]`
        );
      });

      if ((!allPanelsPresent || !allResultsPresent) && attempts < maxAttempts) {
        setTimeout(poll, 250);
        return;
      }

      requestAnimationFrame(() => {
        setTimeout(() => {
          const allSheets = Array.from(container.querySelectorAll(".sheet")).filter(
            el => el.style.visibility !== "hidden" && el.style.position !== "absolute"
          );

          const items = [];
          const resultEntries = new Map();

          allSheets.forEach((sheet, idx) => {
            const pageNum = idx + 1;
            const sectionKey = sheet.dataset.section;
            const sectionType = sheet.dataset.sectionType;
            if (!sectionKey) return;

            if (sectionType === "information-panel") {
              const panel = panelBySectionKey.get(sectionKey);
              if (panel) {
                items.push({
                  kind: "panel",
                  id: panel.id,
                  title: panel.title,
                  page: pageNum,
                  href: `#panel-${panel.id}`,
                  diagramPage: null
                });
              }
              return;
            }

            if (sectionType === "information-panel-diagram") {
              const panel = panelBySectionKey.get(sectionKey);
              if (panel) {
                const panelItem = items.find(item => item.kind === "panel" && item.id === panel.id);
                if (panelItem) {
                  panelItem.diagramPage = pageNum;
                }
              }
              return;
            }

            if (!sectionKey.startsWith("result-")) return;

            const resultId = sectionKey.replace("result-", "");
            if (!resultEntries.has(resultId)) {
              const userResult = userResults.find(ur => String(ur.result.id) === String(resultId));
              const entry = {
                kind: "result",
                id: resultId,
                name: userResult?.result?.name || "Result",
                introPage: null,
                markersPage: null
              };
              resultEntries.set(resultId, entry);
              items.push(entry);
            }

            const entry = resultEntries.get(resultId);
            if (sectionType === "intro" && !entry.introPage) entry.introPage = pageNum;
            if (sectionType === "markers" && !entry.markersPage) entry.markersPage = pageNum;
          });

          setTocItems(items);
        }, 300);
      });
    };

    const timer = setTimeout(poll, 250);
    return () => clearTimeout(timer);
  }, [userResults, printPanels, panelBySectionKey]);

  return (
    <div className="sheet padding-15mm" ref={containerRef}>
      <div className="toc-section">
        <h2>Table of Contents</h2>
        {tocItems ? (
          <div className="toc-columns">
            {tocItems.map(item => {
              if (item.kind === "panel") {
                return (
                  <div key={`panel-${item.id}`} className="toc-result">
                    <a href={item.href} className="toc-entry toc-entry-result">
                      <span className="toc-title">{item.title}</span>
                      <span className="toc-dots" />
                      <span className="toc-page">{item.page}</span>
                    </a>
                    {item.diagramPage ? (
                      <a href={`#panel-${item.id}-diagram`} className="toc-entry toc-entry-sub">
                        <span className="toc-title">Diagram</span>
                        <span className="toc-dots" />
                        <span className="toc-page">{item.diagramPage}</span>
                      </a>
                    ) : null}
                  </div>
                );
              }

              const hasIntro = Boolean(item.introPage);
              const primaryPage = item.introPage || item.markersPage;
              const primaryHref = hasIntro
                ? `#result-${item.id}-intro`
                : `#result-${item.id}-markers`;

              return (
                <div key={`result-${item.id}`} className="toc-result">
                  <a href={primaryHref} className="toc-entry toc-entry-result">
                    <span className="toc-title">{item.name}</span>
                    <span className="toc-dots" />
                    <span className="toc-page">{primaryPage || "—"}</span>
                  </a>
                  {item.markersPage && hasIntro && (
                    <a href={`#result-${item.id}-markers`} className="toc-entry toc-entry-sub">
                      <span className="toc-title">Markers</span>
                      <span className="toc-dots" />
                      <span className="toc-page">{item.markersPage}</span>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p style={{ color: "#999" }}>Building table of contents...</p>
        )}
      </div>
      <PageFooter
        patientName={patientName}
        dateOfBirth={dateOfBirth}
        sex={sex}
        reportDate={reportDate}
      />
    </div>
  );
}

function LoadingPage() {
  return (
    <div className="sheet padding-20mm">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          fontFamily: "Gilroy W05 Medium, sans-serif",
          fontSize: "14pt",
          color: "#666"
        }}
      >
        Loading report data...
      </div>
    </div>
  );
}

// ============================================================================
// Detail Loader - loads details for each result
// ============================================================================

function ResultSectionWithData({
  userResultId,
  result,
  snpsForDiagram,
  expectedSnps,
  userId,
  organisationId,
  userResultPrintDetailsQuery,
  patientName,
  dateOfBirth,
  sex,
  reportDate,
  allResults
}) {
  const { data, loading } = useQuery(userResultPrintDetailsQuery, {
    variables: getUserResultPrintVariables(userResultId, { userId, organisationId }),
    fetchPolicy: "cache-first"
  });
  const [layoutReady, setLayoutReady] = useState(false);

  useEffect(() => {
    setLayoutReady(false);
  }, [userResultId, loading]);

  usePrintSectionReady(`result-${userResultId}`, !loading && layoutReady);

  const handleLayoutReady = useCallback(() => {
    setLayoutReady(true);
  }, []);

  if (loading) {
    return (
      <div className="sheet padding-15mm">
        <div className="result-section">
          <h2>{result.name}</h2>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const details = data?.userResult;

  return (
    <ResultSection
      result={result}
      dnaDatapoints={details?.dnaDatapoints}
      functionalDatapoints={details?.functionalDatapoints}
      untestedDnaDatapoints={details?.untestedDnaDatapoints}
      untestedDatapoints={details?.untestedDatapoints}
      questionsAndAnswers={details?.questionsAndAnswers}
      snpsForDiagram={snpsForDiagram}
      expectedSnps={details?.expectedSnps ?? expectedSnps}
      patientName={patientName}
      dateOfBirth={dateOfBirth}
      sex={sex}
      reportDate={reportDate}
      allResults={allResults}
      onLayoutReady={handleLayoutReady}
    />
  );
}

// ============================================================================
// Main Component
// ============================================================================

function ReportPrintToolbar({ isPrinting, setIsPrinting, onClose }) {
  const isReportReady = usePrintReadiness();

  const handlePrint = useCallback(async () => {
    if (!isReportReady || isPrinting) return;

    setIsPrinting(true);
    try {
      await document.fonts.ready;
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      window.print();
    } finally {
      setIsPrinting(false);
    }
  }, [isReportReady, isPrinting, setIsPrinting]);

  useEffect(() => {
    const handleKeyDown = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === "p") {
        e.preventDefault();
        handlePrint();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handlePrint]);

  const buttonLabel = isPrinting
    ? "Preparing..."
    : isReportReady
    ? "Print Report"
    : "Loading report...";

  return (
    <div className="print-toolbar no-print">
      <button className="print-btn" onClick={handlePrint} disabled={!isReportReady || isPrinting}>
        {buttonLabel}
      </button>
      {onClose && (
        <button className="close-btn" onClick={onClose}>
          Close Preview
        </button>
      )}
    </div>
  );
}

export default function UserReportPrint({
  userId,
  organisationId,
  reportSlug,
  reportPrintQuery,
  userResultPrintDetailsQuery,
  onClose
}) {
  useFullscreenPrint();
  const [isPrinting, setIsPrinting] = useState(false);

  const { data, loading, error } = useQuery(reportPrintQuery, {
    variables: getReportPrintVariables({ reportSlug, userId, organisationId }),
    fetchPolicy: "network-only"
  });

  const report = data?.report;
  const userReport = data?.userReport;
  const user = data?.user;

  const reportName = report?.name || "Report";
  const reportIcon = report?.purchaseProduct?.content?.reportIcon;
  const patientName = user ? `${user.firstName} ${user.lastName}` : "Patient";
  const profile = user?.patientProfile;
  const dateOfBirth = profile?.dateOfBirth
    ? new Date(profile.dateOfBirth).toLocaleDateString("en-GB")
    : null;
  const sex = profile?.gender
    ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1).toLowerCase()
    : null;
  const reportDate = new Date().toLocaleDateString("en-GB");
  const orderNumber = userReport?.displayOrderId ?? userReport?.order?.id;

  const printDocumentTitle =
    loading || error ? null : buildPrintDocumentTitle({ reportName, patientName, reportDate });

  usePrintDocumentTitle(printDocumentTitle);

  const userResultIds = useMemo(
    () => userReport?.userResultConnections?.edges?.map(edge => edge.node.id) || [],
    [userReport?.userResultConnections?.edges]
  );
  const printPanels = useMemo(() => getPrintPanels(report), [report]);
  const { atStart: startPrintPanels, afterResult: afterResultPrintPanels } = useMemo(
    () => partitionPrintPanels(printPanels),
    [printPanels]
  );
  const printPanelIds = useMemo(() => printPanels.map(panel => `panel-${panel.id}`), [printPanels]);
  const printSectionIds = useMemo(
    () =>
      loading || error ? [] : ["toc", ...printPanelIds, ...userResultIds.map(id => `result-${id}`)],
    [loading, error, printPanelIds, userResultIds]
  );

  if (loading) {
    return (
      <div className="print-container A4 report-print">
        <LoadingPage />
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container A4 report-print">
        <div className="sheet padding-20mm">
          <h2>Error loading report</h2>
          <p>{error.message}</p>
        </div>
      </div>
    );
  }

  const userResults = userReport?.userResultConnections?.edges?.map(e => e.node) || [];
  const snpsForDiagram = userReport?.snpsForDiagram || {};
  const expectedSnps = userReport?.expectedSnps || {};
  const allResults = userResults.map(ur => ({
    id: ur.result.id,
    name: ur.result.name,
    hasDiagram: resultHasDiagram(ur.result)
  }));

  return (
    <PrintReadinessProvider sectionIds={printSectionIds}>
      <div className="print-container A4 report-print">
        <ReportPrintToolbar
          isPrinting={isPrinting}
          setIsPrinting={setIsPrinting}
          onClose={onClose}
        />

        {/* Cover Page */}
        <div className="sheet cover-sheet">
          <Cover
            reportIcon={reportIcon}
            reportName={reportName}
            fullName={patientName}
            dateOfBirth={dateOfBirth}
            dateReported={reportDate}
            orderNumber={orderNumber}
          />
        </div>

        {/* Table of Contents */}
        <TableOfContents
          patientName={patientName}
          dateOfBirth={dateOfBirth}
          sex={sex}
          reportDate={reportDate}
          userResults={userResults}
          printPanels={printPanels}
        />

        {startPrintPanels.map(panel => (
          <InformationPanelPrintSheet
            key={panel.id}
            panel={panel}
            snpsForDiagram={snpsForDiagram}
            expectedSnps={expectedSnps}
            allResults={allResults}
            patientName={patientName}
            dateOfBirth={dateOfBirth}
            sex={sex}
            reportDate={reportDate}
          />
        ))}

        {/* Result Sections - one page per result (may overflow to multiple) */}
        {userResults.map(userResult => (
          <Fragment key={userResult.id}>
            <ResultSectionWithData
              userResultId={userResult.id}
              result={userResult.result}
              snpsForDiagram={userResult.snpsForDiagram}
              expectedSnps={expectedSnps}
              userId={userId}
              organisationId={organisationId}
              patientName={patientName}
              dateOfBirth={dateOfBirth}
              sex={sex}
              reportDate={reportDate}
              allResults={allResults}
              userResultPrintDetailsQuery={userResultPrintDetailsQuery}
            />
            {getPanelsAfterResult(afterResultPrintPanels, userResult.result).map(panel => (
              <InformationPanelPrintSheet
                key={panel.id}
                panel={panel}
                snpsForDiagram={snpsForDiagram}
                expectedSnps={expectedSnps}
                allResults={allResults}
                currentResultId={userResult.result.id}
                patientName={patientName}
                dateOfBirth={dateOfBirth}
                sex={sex}
                reportDate={reportDate}
              />
            ))}
          </Fragment>
        ))}
      </div>
    </PrintReadinessProvider>
  );
}
